import React, { useState, useEffect } from "react";
import { Calculator, Layers, Search, FileSpreadsheet } from "lucide-react";
import { C, eur, pct1, pct0 } from "../igualdad-data.js";
import { SectionHead, Status } from "../igualdad-components.jsx";
import { apiGet, apiPost, downloadCsv } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Incrementos salariales
   Selector de escenario · matriz de incrementos · simulación por persona
   ============================================================ */

const ESCENARIOS = ["pesimista", "realista", "optimista"];
const RESULT_LABELS = { A: "A · Excelente", B: "B · Cumple alto", C: "C · Cumple", D: "D · Mejorable" };

function IgualdadIncrementos() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;

  const [escenario, setEscenario] = useState("realista");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [data, setData] = useState(null);

  async function load() {
    if (!politicaId) { setLoading(false); setData(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiGet(`/politicas-retributivas/${politicaId}/incrementos`, { escenario });
      setData(res.data);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }

  // Origen: onMounted(load) + watch(politicaId) + watch(escenario) — 3 disparadores, 1 efecto.
  useEffect(() => { load(); }, [politicaId, escenario]);

  const kpis = data?.kpis || {};
  const matriz = data?.matriz || { valores: {}, banda_buckets: [], result_buckets: [] };
  const rows = (data?.rows || []).filter((x) => x.nombre.toLowerCase().includes(q.toLowerCase()));

  async function finalizar() {
    if (finalizing) return;
    setFinalizing(true);
    try {
      const res = await apiPost(`/politicas-retributivas/${politicaId}/incrementos/finalizar`, { escenario });
      const m = res.data?.modelo;
      fireToast(`Modelo «${escenario}» finalizado · coste ${eur(m?.coste || kpis.coste)} · ${m?.n_suben ?? kpis.n_suben} subidas.`);
    } catch (e) {
      fireToast(e.message || "No se pudo finalizar el modelo.");
    } finally { setFinalizing(false); }
  }

  function exportCsv() {
    downloadCsv(
      `incrementos-${escenario}.csv`,
      ["Persona", "Pos. banda %", "Evaluación", "Matriz %", "Salario actual", "Salario propuesto", "Nueva pos. %"],
      rows.map((x) => [x.nombre, Math.round(x.pos), x.bucket, x.pctInc, Math.round(x.actual), Math.round(x.propuesto), Math.round(x.nuevaPos)])
    );
    fireToast(`Exportadas ${rows.length} filas a CSV.`);
  }

  return (
    <div className="rb-page">
      <div className="rb-pagehead">
        <div><h1 className="rb-h1">Incrementos salariales · {session.politica?.ejercicio}</h1><p className="rb-lead">La matriz cruza el resultado de la evaluación con la posición en banda para proponer el incremento de cada persona.</p></div>
        <button className="rb-btn" disabled={finalizing || loading} onClick={finalizar}>{finalizing ? "Finalizando…" : "Finalizar modelo"}</button>
      </div>

      <div className="rb-seg" style={{ width: "fit-content", marginBottom: "16px" }}>
        <span className="rb-seg-l"><Calculator size={12} /> Escenario</span>
        {ESCENARIOS.map((m) => (
          <button key={m} className={escenario === m ? "on" : ""} onClick={() => setEscenario(m)} style={{ textTransform: "capitalize" }}>{m}</button>
        ))}
      </div>

      {loading || error ? (
        <Status loading={loading} error={error} />
      ) : (
        <>
          <div className="rb-kpis" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <div className="rb-kpi"><div className="rb-kpi-l">Masa salarial</div><div className="rb-kpi-v" style={{ fontSize: "21px" }}>{eur(kpis.masa)}</div><div className="rb-kpi-s">retribución fija actual</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Coste de incrementos</div><div className="rb-kpi-v" style={{ fontSize: "21px", color: C.brand }}>{eur(kpis.coste)}</div><div className="rb-kpi-s">año completo</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">% incremento medio</div><div className="rb-kpi-v">{pct1(kpis.pct_medio || 0)}</div><div className="rb-kpi-s">sobre masa</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Personas con subida</div><div className="rb-kpi-v">{kpis.n_suben}</div><div className="rb-kpi-s">de {kpis.n_personas}</div></div>
          </div>

          <div className="rb-card">
            <SectionHead icon={Layers} title={`Matriz de incrementos · ${escenario}`} />
            <table className="rb-table rb-matrix">
              <thead><tr><th className="rb-l">Resultado evaluación ↓ / Posición banda →</th>{matriz.banda_buckets.map((b) => (<th key={b}>{b}</th>))}</tr></thead>
              <tbody>
                {matriz.result_buckets.map((rb) => (
                  <tr key={rb}>
                    <td className="rb-l rb-strong">{RESULT_LABELS[rb] || rb}</td>
                    {(matriz.valores[rb] || []).map((v, i) => (
                      <td key={i} className="rb-c">
                        <span className="rb-mxcell" style={{ background: v === 0 ? "var(--gray-100)" : `rgba(255,137,1,${0.14 + v / 12})`, color: v === 0 ? C.textMuted : C.charcoal }}>{v === 0 ? "0" : `${String(v).replace(".", ",")} %`}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rb-note">Quien está por debajo de banda y evalúa alto recibe el mayor incremento; quien está por encima de banda no incrementa. Cambia el escenario para recalcular toda la plantilla.</div>
          </div>

          <div className="rb-toolbar">
            <div className="rb-search"><Search size={15} /><input placeholder="Buscar persona…" value={q} onChange={(ev) => setQ(ev.target.value)} /></div>
            <div className="rb-toolbar-r"><span className="rb-count">{rows.length} personas</span><button className="rb-btn-ghost" onClick={exportCsv}><FileSpreadsheet size={14} /> Excel</button></div>
          </div>
          <div className="rb-card rb-card-flat">
            <table className="rb-table">
              <thead><tr><th className="rb-l">Persona</th><th>Pos. banda</th><th>Eval.</th><th>Matriz %</th><th>Salario actual</th><th>Salario propuesto</th><th>Nueva pos.</th></tr></thead>
              <tbody>
                {rows.slice(0, 40).map((x) => (
                  <tr key={x.persona_id}>
                    <td className="rb-l rb-strong">{x.nombre}</td>
                    <td className="rb-mono">{pct0(x.pos)}</td>
                    <td><span className="rb-evchip" data-b={x.bucket}>{x.bucket}</span></td>
                    <td className="rb-mono" style={{ color: x.pctInc > 0 ? C.brand : C.textMuted, fontWeight: 600 }}>{x.pctInc > 0 ? `+${String(x.pctInc).replace(".", ",")} %` : "—"}</td>
                    <td className="rb-mono">{eur(x.actual)}</td>
                    <td className="rb-mono rb-strong">{eur(x.propuesto)}</td>
                    <td className="rb-mono">{pct0(x.nuevaPos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 40 && <div className="rb-tablefoot">Mostrando 40 de {rows.length}</div>}
          </div>
        </>
      )}
    </div>
  );
}

export { IgualdadIncrementos };
